//* Copie e cole este template para criar uma nova fase
<template>
  <div v-if="!!phaseData">
    <v-sheet v-if="loading || error" class="d-flex flex-column align-center justify-center row-gap-2" height="50vh">
      <template v-if="error">
        <h3>Um erro ocorreu ao tentar carregar os dados da tela!</h3>
        <v-btn class="flex-grow-0" large color="primary" @click="loadData" :loading="loading"
          >tentar novamente
          <v-icon class="ml-2" large>mdi-refresh</v-icon>
        </v-btn>
      </template>
      <template v-else>
        <h3>Carregando dados...</h3>
        <v-progress-circular indeterminate />
      </template>
    </v-sheet>
    <div v-else>
      <radio-options
        label="O paciente tem diagnóstico de psoríase?"
        :options="diagnosisOptions"
        row
        v-model="phaseData['diagnostico_psoriase'].value"
        hide-details
        @input="phaseData['ano_diagnostico_psoriase'].value = null"
        clearable
        :rules="pharmaceuticalDomainConfig.SUMMARIZEDFLOW ? [$validation.radioRequired()] : []"
        :required="pharmaceuticalDomainConfig.SUMMARIZEDFLOW"
      />

      <v-row>
        <v-col cols="12" sm="6">
          <v-autocomplete
            v-if="phaseData['diagnostico_psoriase'].value"
            placeholder="Ano do diagnóstico"
            v-model="phaseData['ano_diagnostico_psoriase'].value"
            outlined
            append-icon="mdi-calendar"
            class="mt-4 pa-0"
            :items="getDiagnosisYears"
            hide-details
            :rules="pharmaceuticalDomainConfig.SUMMARIZEDFLOW ? [$validation.radioRequired()] : []"
            :required="pharmaceuticalDomainConfig.SUMMARIZEDFLOW"
          >
          </v-autocomplete>
          <span v-if="phaseData['diagnostico_psoriase'].value" class="mt-2 ml-5">AAAA</span>
        </v-col>
      </v-row>
      <field-label
        label="Quando iniciaram os sintomas?"
        class="mt-4 mb-4"
        :required="pharmaceuticalDomainConfig.SUMMARIZEDFLOW"
      >
        <template #label-extra>
          <v-btn x-small text color="primary" @click="() => (phaseData.inicio_sintomas = null)">limpar</v-btn>
        </template>
        <v-row>
          <v-col cols="12" sm="6">
            <v-menu
              ref="pickDate"
              v-model="pickDate"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="auto"
              :required="pharmaceuticalDomainConfig.SUMMARIZEDFLOW"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="phaseData.inicio_sintomas ? $moment(phaseData.inicio_sintomas).format('MM/YYYY') : ''"
                  outlined
                  v-bind="attrs"
                  v-on="on"
                  placeholder="Data"
                  append-icon="mdi-calendar"
                  hide-details
                />
              </template>
              <v-date-picker
                v-model="phaseData.inicio_sintomas"
                no-title
                type="month"
                locale="pt-br"
                @click:month="pickDate = false"
                :max="$moment().toISOString()"
              ></v-date-picker>
            </v-menu>
            <span class="mt-2 ml-5" style="color: #00000099">MM/AAAA</span>
          </v-col>
        </v-row>
      </field-label>
      <v-row>
        <v-col cols="12">
          <field-label label="Tratamentos realizados">
            <v-autocomplete
              outlined
              label="Selecionar tratamentos"
              item-value="id"
              item-text="name"
              multiple
              chips
              deletable-chips
              hide-selected
              small-chips
              return-object
              menu-props="closeOnContentClick"
              v-model="selectedMedicines"
              :items="medicines"
            >
              <template v-slot:selection="data">
                <v-chip
                  outlined
                  close-icon="mdi-close"
                  :input-value="data.selected"
                  close
                  @click:close="toggleMedicine(data.item, false)"
                >
                  {{ data.item.name }}
                </v-chip>
              </template>
              <template v-slot:no-data>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title
                      >Não foi encontrado nenhum registro. Verifique a ortografia e tente novamente.
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item v-if="!hasOtherMedicines" @click="addOther">
                  <v-list-item-content>
                    <v-list-item-title>Outros tratamentos</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-autocomplete>
          </field-label>
        </v-col>
      </v-row>
      <section class="mt-4" v-for="medicine in selectedMedicines" :key="`medicine-${medicine.id}`">
        <template v-if="!(medicine.category.id === medicineCategories.TOPICOS && attendance.data.summarizedFlow)">
          <v-card outlined rounded="lg">
            <v-card-title class="justify-space-between">
              {{ medicine.name }}
              <v-btn icon title="Apagar período" @click="toggleMedicine(medicine, false)">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <field-label
              class="pa-4"
              label="Períodos anteriores"
              v-if="phaseData.medicines[medicine.id] && phaseData.medicines[medicine.id].periods.length > 0"
            >
              <ul class="py-2">
                <li
                  v-for="(period, periodIndex) in phaseData.medicines[medicine.id].periods"
                  :key="`period-${medicine.id}-${period.start}`"
                >
                  {{ buildPeriodString(period) }}
                  <v-btn icon small title="Remover período" @click="removePeriod(medicine.id, periodIndex)">
                    <v-icon>mdi-trash-can-outline</v-icon>
                  </v-btn>
                </li>
              </ul>
            </field-label>
            <v-form ref="addPeriodForm" v-model="phaseData.medicines[medicine.id].currentPeriod.valid">
              <v-container>
                <div v-if="medicine.category.id === medicineCategories.FOTOTERAPICOS">
                  <v-text-field
                    type="number"
                    outlined
                    placeholder="Número de sessões de fototerapia realizadas"
                    :mask="['###']"
                    v-model="phaseData['numero_sessoes_fototerapia'].value"
                    class="mt-2"
                    :required="pharmaceuticalDomainConfig.SUMMARIZEDFLOW"
                  />
                </div>
                <div v-else-if="medicine.category.id === medicineCategories.OTHERS">
                  <v-combobox
                    multiple
                    outlined
                    small-chips
                    deletable-chips
                    chips
                    persistent-hint
                    append-icon=""
                    hint="Digite o tratamento e clique 'Enter' para adicionar"
                    placeholder="Outros tratamentos"
                    persistent-placeholder
                    v-model="phaseData['outros_tratamentos'].value"
                  />
                </div>
                <div v-else>
                  <div
                    v-if="phaseData.medicines[medicine.id] && phaseData.medicines[medicine.id].periods.length > 0"
                    class="subtitle-2 font-weight-medium mb-4"
                  >
                    Período atual
                  </div>
                  <radio-options
                    clearable
                    v-if="medicine.category.id === medicineCategories.TOPICOS"
                    label="Usou por mais de 3 meses contínuos, sem melhora efetiva?"
                    v-model="phaseData.medicines[medicine.id]['medicamentos_previos_mais_de_3_meses']"
                    row
                    :options="defaultYesNoOptions"
                    required
                  />
                  <div class="subtitle-1 font-weight-medium mb-4">Posologia</div>
                  <v-row>
                    <v-col cols="6" class="py-0">
                      <v-text-field
                        outlined
                        type="number"
                        placeholder="Dose máxima (mg)"
                        v-model="phaseData.medicines[medicine.id].currentPeriod.dosage"
                        :rules="[$validation.minValue(0, false)]"
                      />
                    </v-col>
                    <v-col class="py-0">
                      <v-select
                        outlined
                        :items="defaultPeriodOptions"
                        placeholder="Período"
                        v-model="phaseData.medicines[medicine.id].currentPeriod.period"
                      />
                    </v-col>

                    <v-col
                      cols="3"
                      class="py-0"
                      v-if="phaseData.medicines[medicine.id].currentPeriod.period === 'CADA_N_SEMANAS'"
                    >
                      <v-text-field
                        outlined
                        label="Núm. semanas"
                        v-mask="['##']"
                        v-model="phaseData.medicines[medicine.id].currentPeriod.length"
                        :rules="[$validation.minValue(0, false)]"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="6" class="py-0">
                      <v-menu
                        v-model="phaseData.medicines[medicine.id].currentPeriod.startPickDate"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        max-width="290px"
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            outlined
                            v-bind="attrs"
                            v-on="on"
                            :dense="false"
                            placeholder="Data de início"
                            append-icon="mdi-calendar"
                            v-mask="['##/##/####']"
                            v-model="phaseData.medicines[medicine.id].currentPeriod.startFormatted"
                            @blur="parseMedicineDate(medicine, 'start')"
                            :rules="[
                              $validation.validDate(),
                              $validation.dateBefore(null, true),
                              periodStartBeforeEndValidation(medicine.id),
                            ]"
                          />
                        </template>
                        <v-date-picker
                          v-model="phaseData.medicines[medicine.id].currentPeriod.start"
                          no-title
                          locale="pt-br"
                          @input="formatMedicineDate(medicine, 'start')"
                          :max="$moment().toISOString()"
                        ></v-date-picker>
                      </v-menu>
                    </v-col>
                    <v-col cols="6" class="py-0">
                      <v-menu
                        v-model="phaseData.medicines[medicine.id].currentPeriod.endPickDate"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        max-width="290px"
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            outlined
                            v-bind="attrs"
                            v-on="on"
                            v-mask="['##/##/####']"
                            hint="Deixe em branco se estiver em uso"
                            persistent-hint
                            append-icon="mdi-calendar"
                            placeholder="Data de fim"
                            v-model="phaseData.medicines[medicine.id].currentPeriod.endFormatted"
                            @blur="parseMedicineDate(medicine, 'end')"
                            :rules="[$validation.validDate(), $validation.dateBefore(null, true)]"
                          />
                        </template>
                        <v-date-picker
                          v-model="phaseData.medicines[medicine.id].currentPeriod.end"
                          no-title
                          locale="pt-br"
                          @input="formatMedicineDate(medicine, 'end')"
                          :max="$moment().toISOString()"
                        ></v-date-picker>
                      </v-menu>
                    </v-col>
                    <v-col cols="12" v-if="!!phaseData.medicines[medicine.id].currentPeriod.end" class="py-0">
                      <select-field
                        outlined
                        placeholder="Motivo"
                        :items="stopReasons"
                        item-text="name"
                        item-value="id"
                        v-model="phaseData.medicines[medicine.id].currentPeriod.stopReason"
                        return-object
                        multiple
                      />
                    </v-col>
                    <v-col class="py-0">
                      <text-field
                        label="Outros motivos"
                        v-model="phaseData.medicines[medicine.id].currentPeriod.otherStopReason"
                        v-if="
                          phaseData.medicines[medicine.id].currentPeriod.stopReason.some((r) => r.name === 'Outros')
                        "
                      />
                    </v-col>
                  </v-row>

                  <div class="subtitle-1 font-weight-medium mb-4">Efeitos adversos</div>
                  <v-row>
                    <v-col class="py-0">
                      <v-combobox
                        multiple
                        outlined
                        small-chips
                        deletable-chips
                        chips
                        :items="adverseEffects"
                        placeholder="Descreva os efeitos adversos"
                        persistent-placeholder
                        v-model="phaseData.medicines[medicine.id].currentPeriod.event"
                      />
                    </v-col>
                    <v-col class="py-0" v-if="phaseData.medicines[medicine.id].currentPeriod.event">
                      <v-menu
                        v-model="phaseData.medicines[medicine.id].currentPeriod.eventDatePickDate"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        max-width="290px"
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            outlined
                            v-bind="attrs"
                            v-on="on"
                            v-mask="['##/##/####']"
                            placeholder="Data do efeito"
                            append-icon="mdi-calendar"
                            v-model="phaseData.medicines[medicine.id].currentPeriod.eventDateFormatted"
                            @blur="parseMedicineDate(medicine, 'eventDate')"
                            :rules="[$validation.validDate()]"
                          />
                        </template>
                        <v-date-picker
                          v-model="phaseData.medicines[medicine.id].currentPeriod.eventDate"
                          no-title
                          locale="pt-br"
                          @input="formatMedicineDate(medicine, 'eventDate')"
                          :max="$moment().toISOString()"
                        ></v-date-picker>
                      </v-menu>
                    </v-col>
                  </v-row>
                </div>
              </v-container>
            </v-form>
            <v-card-actions
              v-if="![medicineCategories.FOTOTERAPICOS, medicineCategories.OTHERS].includes(medicine.category.id)"
            >
              <v-btn
                class="text-none"
                color="primary"
                text
                @click="addPeriod(medicine.id)"
                :disabled="!phaseData.medicines[medicine.id].currentPeriod.valid"
                >Registrar outro período
              </v-btn>
            </v-card-actions>
          </v-card>
        </template>
      </section>
      <attendance-step-note class="mt-8 py-2" />
    </div>
  </div>
</template>
<script>
import FieldLabel from '@/components/FieldLabel.vue'
import RadioOptions from '@/components/RadioOptions.vue'
import SelectField from '@/components/SelectField.vue'
import TextField from '@/components/TextField.vue'
import { MEDICINE_CATEGORIES } from '@/constants/medicine'
import BasePhase from '../../../general/phase/BasePhase.vue'

import { v4 as uuidv4 } from 'uuid'
import { createNamespacedHelpers } from 'vuex'
import { mask } from 'vue-the-mask'
import AttendanceStepNote from '@/components/AttendanceForm/psoriasis/phase/v2/AttendanceStepNote.vue'

const { mapGetters: attendanceGettersMap } = createNamespacedHelpers('attendance')
const { mapGetters: authGettersMap } = createNamespacedHelpers('auth')

export default {
  components: {
    AttendanceStepNote,
    RadioOptions,
    FieldLabel,
    TextField,
    SelectField,
  },
  directives: {
    mask: {
      bind(el, binding, vnode, oldVnode) {
        if (binding.value) {
          mask(el, binding, vnode, oldVnode)
        }
      },
    },
  },
  extends: BasePhase,
  computed: {
    ...attendanceGettersMap(['attendanceDiseaseId', 'attendance']),
    ...authGettersMap(['pharmaceuticalDomainConfig']),
    hasOtherMedicines() {
      return this.selectedMedicines.some((m) => m.category.id === MEDICINE_CATEGORIES.OTHERS)
    },
    defaultYesNoOptions() {
      return [
        { text: 'Sim', value: true },
        { text: 'Não', value: false },
      ]
    },
    diagnosisOptions() {
      return [
        { text: 'Sim', value: true },
        { text: 'Em investigação', value: false },
      ]
    },
    defaultPeriodOptions() {
      return [
        { text: 'por semana', value: 'POR_SEMANA' },
        { text: 'por dia', value: 'POR_DIA' },
        { text: 'a cada (n) semanas', value: 'CADA_N_SEMANAS' },
      ]
    },
    medicinesByCategory() {
      const medicinesByCategory = {}
      for (const medicine of this.medicines) {
        if (!medicinesByCategory[medicine.category.id]) {
          medicinesByCategory[medicine.category.id] = {
            category: medicine.category,
            medicines: [],
          }
        }

        medicinesByCategory[medicine.category.id].medicines.push(medicine)
      }

      return medicinesByCategory
    },
    sortedCategoryKeys() {
      const order = {}
      order[MEDICINE_CATEGORIES.TOPICOS] = 1
      order[MEDICINE_CATEGORIES.SISTEMICOS_TRADICIONAIS] = 2
      order[MEDICINE_CATEGORIES.IMUNOBIOLOGICOS] = 3
      return Object.entries(this.medicinesByCategory)
        .sort((entry1, entry2) => order[entry1[1].category.id] - order[entry2[1].category.id])
        .map((entry) => entry[0])
    },
    getDiagnosisYears() {
      const age = this.$util.toAgeNumber(this.attendance.patient.birthday)
      let years = []
      for (let i = age; i >= 0; i--) {
        years.push(this.$moment().year() - i)
      }
      return years.reverse()
    },
    medicineCategories() {
      return MEDICINE_CATEGORIES
    },
  },
  data() {
    return {
      loading: true,
      error: false,
      medicines: [],
      selectedMedicines: null,
      stopReasons: [],
      periodModels: {},
      medicineModels: {},
      adverseEffects: [],
      pickDate: false,
      defaultData: {
        diagnostico_psoriase: { value: null },
        ano_diagnostico_psoriase: { value: null },
        inicio_sintomas: null,
        realizou_sessoes_fototerapia: { value: null },
        numero_sessoes_fototerapia: { value: null },
        outros_tratamentos: { value: null },
        biopsia_previa: { value: null },
        medicines: {},
        notes: null,
      },
    }
  },
  watch: {
    selectedMedicines(newArr, oldArr) {
      if (!oldArr) {
        // First initialization
        newArr.forEach((item) => this.toggleMedicine(item, true))
        return
      }
      const addedItem = newArr.find((item) => !oldArr.includes(item))
      const removedItem = oldArr.find((item) => !newArr.includes(item))

      if (addedItem !== undefined) {
        this.toggleMedicine(addedItem, true)
      } else if (removedItem !== undefined) {
        this.$delete(this.phaseData.medicines, removedItem.id)
      } else {
        console.log('No items were added or removed.')
      }
    },
  },
  methods: {
    getDefaultData() {
      return this.defaultData
    },
    genNewPeriodModel() {
      return {
        valid: false,
        dosage: null,
        stopReason: [],
        period: null,
        length: null,
        start: null,
        startFormatted: null,
        startPickDate: false,
        end: null,
        endFormatted: null,
        endPickDate: false,
        eventDate: null,
        eventDateFormatted: null,
        eventDatePickDate: false,
      }
    },
    genNewMedicineModel(valid = false) {
      return {
        valid,
        name: null,
      }
    },
    async loadData() {
      try {
        this.loading = true

        const medicineStopReasons = await this.$api.getMedicineStopReasons()
        const medicinesForDiseaseTreatment = await this.$api.getMedicinesForDiseaseTreatment(this.attendanceDiseaseId)
        const adverseEffects = await this.$api.listCrud('crud/adverseEffect')

        this.stopReasons = medicineStopReasons
        this.mergeMedicines(medicinesForDiseaseTreatment.items)
        this.medicines.sort((a, b) => a.name.localeCompare(b.name))
        this.selectedMedicines = Object.keys(this.phaseData.medicines).map(
          (key) => this.phaseData.medicines[key].medicine,
        )
        this.adverseEffects = adverseEffects.items.map((item) => item.name)
        this.error = false
      } catch (error) {
        this.$root.error(error)
        this.error = true
      } finally {
        this.loading = false
      }
    },
    mergeMedicines(medicines) {
      const medicineIdsSet = new Set(medicines.map((medicine) => medicine.id))
      this.medicines = medicines.concat(
        Object.values(this.phaseData.medicines)
          .filter((phaseDataMedicine) => !medicineIdsSet.has(phaseDataMedicine.medicine.id))
          .map((selectedMedicine) => selectedMedicine.medicine),
      )
      this.medicines.forEach((medicine) => {
        this.$set(this.periodModels, medicine.id, this.genNewPeriodModel())
      })
      this.sortedCategoryKeys.forEach((categoryId) =>
        this.$set(this.medicineModels, categoryId, this.genNewMedicineModel()),
      )
    },
    async toggleMedicine(medicine, value) {
      if (value) {
        if (medicine.category.id === MEDICINE_CATEGORIES.FOTOTERAPICOS) {
          this.phaseData['numero_sessoes_fototerapia'].value = null
        }

        if (medicine.category.id === MEDICINE_CATEGORIES.OTHERS) {
          this.phaseData['outros_tratamentos'].value = null
        }

        this.$set(this.phaseData.medicines, medicine.id, {
          medicine,
          currentPeriod: this.phaseData.medicines[medicine.id]?.currentPeriod ?? this.genNewPeriodModel(),
          periods: [],
        })
      } else {
        if (this.phaseData.medicines[medicine.id]?.periods?.length > 0) {
          if (!(await this.$root.confirm('Os períodos de uso deste medicamento serão apagados. Deseja continuar?'))) {
            return
          }
        }

        this.selectedMedicines.splice(
          this.selectedMedicines.findIndex((i) => i.id === medicine.id),
          1,
        )
        this.$delete(this.phaseData.medicines, medicine.id)
      }
    },
    addPeriod(medicineId) {
      const currentPeriod = this.phaseData.medicines[medicineId].currentPeriod
      const todayIso = this.$moment().format('YYYY-MM-DD')
      const start = currentPeriod.start
      const end = currentPeriod.end

      const overlaps = this.phaseData.medicines[medicineId].periods.some((period) =>
        this.$util.periodsOverlap(start, end || todayIso, period.start, period.end || todayIso),
      )
      if (overlaps) {
        this.$root.warn('O período informado se sobrepõe a outro período já informado. Verifique!')
        return
      }

      this.phaseData.medicines[medicineId].periods.push({
        dosage: parseInt(currentPeriod.dosage) || null,
        stopReason: currentPeriod.end
          ? currentPeriod.stopReason
              .map((r) => (r.name === 'Outros' ? currentPeriod.otherStopReason : r.name))
              .join(', ')
          : null,
        period: currentPeriod.period,
        events: {
          selected: currentPeriod.event,
          date: this.$moment(currentPeriod.eventDate).format('DD/MM/YYYY'),
        },
        length: currentPeriod.period === 'CADA_N_SEMANAS' ? parseInt(currentPeriod.length) || 0 : null,
        start,
        end,
      })

      this.phaseData.medicines[medicineId].periods.sort(this.$util.objectSorter((period) => period.start))
      this.phaseData.medicines[medicineId].currentPeriod = this.genNewPeriodModel()
      this.$refs.addPeriodForm.forEach((form) => form.resetValidation())
    },
    removePeriod(medicineId, index) {
      this.phaseData.medicines[medicineId].periods.splice(index, 1)
    },
    addMedicine(categoryId) {
      const id = uuidv4()
      const name = this.medicineModels[categoryId].name
      const category = this.$util.copy(this.medicinesByCategory[categoryId].category)

      this.$set(this.periodModels, id, this.genNewPeriodModel())
      this.medicines.push({
        id,
        name,
        category,
      })

      this.medicineModels[categoryId] = this.genNewMedicineModel(true)

      this.$refs.addMedicineForm.forEach((form) => form.resetValidation())
    },
    buildPeriodString(period) {
      let string = ''
      if (!period.start && !period.dosage) {
        string = 'Período e dosagem não informados.'
        return string
      }

      let recurrence = 'por semana'
      if (period.period === 'DIAS') {
        recurrence = 'por dia'
      } else if (period.period === 'CADA_N_SEMANAS') {
        recurrence = `a cada ${period.length} `
        recurrence += period.length > 1 ? 'semanas' : 'semana'
      }

      if (!period.start) {
        string = `${period.dosage}mg ${recurrence} sem período informado.`
        return string
      }

      string =
        (period.dosage ? `${period.dosage}mg ${recurrence}, ` : 'Uso ') +
        `de ${this.$util.toFormattedDate(period.start)} até ${this.$util.toFormattedDate(period.end) || 'atualmente'}`

      if (period.stopReason) {
        string += ` - Motivo da parada: ${period.stopReason.name ?? period.stopReason ?? 'não informado'}`
      }

      if (period.events?.selected?.length) {
        if (period.events.selected?.length > 0) {
          string += ` ${
            period.events.selected?.length > 1 ? 'com eventos adversos:' : 'com evento adverso:'
          } ${period.events.selected.join(', ')} ${
            period.events.date ? 'na data ' + period.events.date : 'sem data informada'
          }`
        }
      }

      return string
    },
    periodStartBeforeEndValidation(medicineId) {
      const currentPeriod = this.phaseData.medicines[medicineId].currentPeriod
      return (
        !currentPeriod.start ||
        !currentPeriod.end ||
        currentPeriod.start.length !== 10 ||
        currentPeriod.end.length !== 10 ||
        this.$util.toIsoDate(currentPeriod.start) <= this.$util.toIsoDate(currentPeriod.end) ||
        'Deve ser anterior ao fim'
      )
    },
    async postPhaseDataLoaded() {
      for (const key in this.defaultData) {
        this.$set(this.phaseData, key, this.phaseData[key] === undefined ? this.defaultData[key] : this.phaseData[key])
      }
      await this.loadData()
    },
    addOther() {
      const id = uuidv4()
      let other = {
        id,
        name: 'Outros tratamentos',
        category: {
          id: MEDICINE_CATEGORIES.OTHERS,
          name: 'Others',
        },
      }
      this.selectedMedicines.push(other)
      this.toggleMedicine(other, true)
      // this.$set(this.periodModels, id, this.genNewPeriodModel())
      this.medicines.push(other)
      this.medicineModels[MEDICINE_CATEGORIES.OTHERS] = this.genNewMedicineModel(true)
    },
    parseMedicineDate(medicine, key) {
      this.phaseData.medicines[medicine.id].currentPeriod[key] = this.$util.toIsoDate(
        this.phaseData.medicines[medicine.id].currentPeriod[`${key}Formatted`],
      )
    },
    formatMedicineDate(medicine, key) {
      this.phaseData.medicines[medicine.id].currentPeriod[`${key}PickDate`] = false
      this.phaseData.medicines[medicine.id].currentPeriod[`${key}Formatted`] = this.$moment(
        this.phaseData.medicines[medicine.id].currentPeriod[key],
      ).format('DD/MM/YYYY')
    },
  },
}
</script>

<style scoped>
:deep(.v-select.v-select--is-menu-active .v-input__icon--append .v-icon.mdi-calendar) {
  transform: none !important;
}
</style>
